import React from 'react';

interface ErrorStateProps {
  title: string;
  message: string;
  icon?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  retryLabel?: string;
  className?: string;
  variant?: 'default' | 'inline' | 'toast';
}

// Base error state component
export const ErrorState: React.FC<ErrorStateProps> = ({
  title,
  message,
  icon = '⚠️',
  onRetry,
  onDismiss,
  retryLabel = 'Try Again',
  className = '',
  variant = 'default'
}) => {
  if (variant === 'inline') {
    return (
      <div className={`bg-rust-50 border border-rust-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-start gap-3">
          <span className="text-rust text-lg flex-shrink-0">{icon}</span>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-rust-800 mb-1">{title}</h4>
            <p className="text-rust-700 text-sm mb-3">{message}</p>
            <div className="flex gap-2">
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="bg-rust hover:bg-rust-600 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors"
                >
                  {retryLabel}
                </button>
              )}
              {onDismiss && (
                <button
                  onClick={onDismiss}
                  className="text-rust-600 hover:text-rust-800 px-3 py-1.5 text-sm font-medium transition-colors"
                >
                  Dismiss
                </button>
              )}
            </div>
          </div>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-rust-400 hover:text-rust-600 flex-shrink-0"
            >
              ✕
            </button>
          )}
        </div>
      </div>
    );
  }

  if (variant === 'toast') {
    return (
      <div className={`bg-white border-l-4 border-rust shadow-lg rounded-r-lg p-4 max-w-md ${className}`}>
        <div className="flex items-center gap-3">
          <span className="text-rust text-lg">{icon}</span>
          <div className="flex-1">
            <h4 className="font-medium text-urban mb-1">{title}</h4>
            <p className="text-urban-400 text-sm">{message}</p>
          </div>
          <div className="flex gap-2">
            {onRetry && (
              <button
                onClick={onRetry}
                className="text-rust hover:text-rust-600 text-sm font-medium"
              >
                Retry
              </button>
            )}
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-urban-400 hover:text-urban-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default centered error state
  return (
    <div className={`text-center py-16 px-4 ${className}`}>
      <div className="max-w-md mx-auto">
        <div className="text-6xl mb-6 opacity-50">{icon}</div>
        <h3 className="text-xl font-semibold text-urban mb-3">{title}</h3>
        <p className="text-urban-400 mb-8 leading-relaxed">{message}</p>
        <div className="flex gap-3 justify-center">
          {onRetry && (
            <button
              onClick={onRetry}
              className="bg-cardboard hover:bg-cardboard-600 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
            >
              {retryLabel}
            </button>
          )}
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="bg-white border-2 border-urban-200 text-urban hover:bg-urban-50 px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Network/Connection errors
export const NetworkError: React.FC<{
  onRetry?: () => void;
  variant?: 'default' | 'inline' | 'toast';
}> = ({ onRetry, variant = 'default' }) => (
  <ErrorState
    icon="📡"
    title="Connection Error"
    message="We're having trouble connecting to our servers. Please check your internet connection and try again."
    onRetry={onRetry}
    variant={variant}
  />
);

// API/Server errors
export const ServerError: React.FC<{
  onRetry?: () => void;
  variant?: 'default' | 'inline' | 'toast';
  statusCode?: number;
}> = ({ onRetry, variant = 'default', statusCode }) => (
  <ErrorState
    icon="🔧"
    title="Server Error"
    message={`Something went wrong on our end${statusCode ? ` (Error ${statusCode})` : ''}. Our team has been notified and is working on a fix.`}
    onRetry={onRetry}
    variant={variant}
  />
);

// Authentication errors
export const AuthError: React.FC<{
  onRetry?: () => void;
  onLogin?: () => void;
  variant?: 'default' | 'inline' | 'toast';
}> = ({ onRetry, onLogin, variant = 'default' }) => (
  <ErrorState
    icon="🔒"
    title="Authentication Required"
    message="You need to be logged in to access this feature. Please sign in to continue."
    onRetry={onLogin || onRetry}
    retryLabel="Sign In"
    variant={variant}
  />
);

// Permission/Access errors
export const PermissionError: React.FC<{
  onRetry?: () => void;
  variant?: 'default' | 'inline' | 'toast';
  resource?: string;
}> = ({ onRetry, variant = 'default', resource = 'this content' }) => (
  <ErrorState
    icon="🚫"
    title="Access Denied"
    message={`You don't have permission to access ${resource}. Contact support if you believe this is an error.`}
    onRetry={onRetry}
    variant={variant}
  />
);

// Not found errors
export const NotFoundError: React.FC<{
  onRetry?: () => void;
  onGoBack?: () => void;
  variant?: 'default' | 'inline' | 'toast';
  resource?: string;
}> = ({ onRetry, onGoBack, variant = 'default', resource = 'page' }) => (
  <ErrorState
    icon="🔍"
    title="Not Found"
    message={`The ${resource} you're looking for doesn't exist or may have been moved.`}
    onRetry={onGoBack || onRetry}
    retryLabel={onGoBack ? 'Go Back' : 'Try Again'}
    variant={variant}
  />
);

// Form validation errors
export const FormError: React.FC<{
  errors: string[];
  onDismiss?: () => void;
}> = ({ errors, onDismiss }) => (
  <div className="bg-rust-50 border border-rust-200 rounded-lg p-4">
    <div className="flex items-start gap-3">
      <span className="text-rust text-lg flex-shrink-0">⚠️</span>
      <div className="flex-1">
        <h4 className="font-medium text-rust-800 mb-2">Please fix the following errors:</h4>
        <ul className="text-rust-700 text-sm space-y-1">
          {errors.map((error, index) => (
            <li key={index} className="flex items-start gap-2">
              <span className="text-rust-400 mt-0.5">•</span>
              <span>{error}</span>
            </li>
          ))}
        </ul>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="text-rust-400 hover:text-rust-600 flex-shrink-0"
        >
          ✕
        </button>
      )}
    </div>
  </div>
);

// Upload errors
export const UploadError: React.FC<{
  fileName: string;
  error: string;
  onRetry?: () => void;
  onRemove?: () => void;
}> = ({ fileName, error, onRetry, onRemove }) => (
  <div className="bg-rust-50 border border-rust-200 rounded-lg p-3">
    <div className="flex items-center gap-3">
      <span className="text-rust">⚠️</span>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-rust-800 text-sm truncate">{fileName}</p>
        <p className="text-rust-600 text-xs">{error}</p>
      </div>
      <div className="flex gap-2">
        {onRetry && (
          <button
            onClick={onRetry}
            className="text-rust hover:text-rust-600 text-xs font-medium"
          >
            Retry
          </button>
        )}
        {onRemove && (
          <button
            onClick={onRemove}
            className="text-rust-400 hover:text-rust-600 text-xs"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  </div>
);

// Rate limit errors
export const RateLimitError: React.FC<{
  onRetry?: () => void;
  retryAfter?: number; // seconds
  variant?: 'default' | 'inline' | 'toast';
}> = ({ onRetry, retryAfter, variant = 'default' }) => {
  const message = retryAfter 
    ? `You've made too many requests. Please wait ${retryAfter} seconds before trying again.`
    : "You've made too many requests. Please wait a moment before trying again.";

  return (
    <ErrorState
      icon="⏱️"
      title="Rate Limit Exceeded"
      message={message}
      onRetry={onRetry}
      variant={variant}
    />
  );
};

// Generic error boundary fallback
export const ErrorBoundaryFallback: React.FC<{
  error: Error;
  onRetry?: () => void;
}> = ({ error: _error, onRetry }) => (
  <ErrorState
    icon="💥"
    title="Something went wrong"
    message="An unexpected error occurred. Our team has been notified. Please try refreshing the page."
    onRetry={onRetry}
    retryLabel="Refresh Page"
  />
);

// Timeout errors
export const TimeoutError: React.FC<{
  onRetry?: () => void;
  variant?: 'default' | 'inline' | 'toast';
}> = ({ onRetry, variant = 'default' }) => (
  <ErrorState
    icon="⏰"
    title="Request Timeout"
    message="The request is taking longer than expected. Please check your connection and try again."
    onRetry={onRetry}
    variant={variant}
  />
);

// Maintenance mode error
export const MaintenanceError: React.FC<{
  estimatedTime?: string;
}> = ({ estimatedTime }) => (
  <ErrorState
    icon="🔧"
    title="Under Maintenance"
    message={`We're currently performing scheduled maintenance to improve your experience. ${
      estimatedTime ? `Expected completion: ${estimatedTime}` : 'Please check back shortly.'
    }`}
  />
);

// Error toast container (for multiple toasts)
export const ErrorToastContainer: React.FC<{
  errors: Array<{
    id: string;
    title: string;
    message: string;
    onRetry?: () => void;
    onDismiss?: () => void;
  }>;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}> = ({ errors, position = 'top-right' }) => {
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4'
  };

  if (errors.length === 0) return null;

  return (
    <div className={`fixed ${positionClasses[position]} z-50 space-y-2`}>
      {errors.map((error) => (
        <ErrorState
          key={error.id}
          title={error.title}
          message={error.message}
          onRetry={error.onRetry}
          onDismiss={error.onDismiss}
          variant="toast"
          className="animate-slide-down"
        />
      ))}
    </div>
  );
};